import {FC, useContext, useEffect, useRef, useState} from 'react';
import {useForm, SubmitHandler} from 'react-hook-form';
import {useRecoilState, useSetRecoilState} from 'recoil';
import {toast} from 'react-toastify';

import {Paper, Stack, Typography} from '@mui/material';
import ControlledInput from '../../../ui/controlled-input/ControlledInput';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import {UserRowOfGroup} from './UserRowOfGroup';
import {ModalContainer} from '../../modals/ModalContainer';
import {ConfirmAction} from '../../modals/ConfirmAction';
import {ButtonSwitcher} from './ButtonSwitcher';

import {EventBusContext} from '../../../providers/EventBus';

import {IWorkerSelectOption} from '../../../api/strategies/searchWorkersStrategy';
import {IGroupSelectOption} from '../../../api/strategies/searchGroupsStrategy';

import {AccountGroupsService} from '../../../api/services/user-account-groups.service';
import {UserAccountService} from '../../../api/services/user-account.service';

import {
  accountGroupControlModeState,
  accountGroupUsersSelectionToRegroupState,
  accountGroupsQueryTrigger,
  currentAccountGroupIdState,
  editGroupModeState,
} from '../../../store/user-groups/atoms';

import {getFullName} from '../../../shared/helpers/getFullName';

import {innerListStyle} from '../../../shared/styles';
import {IUserAccount} from '../../../shared/types/user-account.types';
import {IAccountGroup} from '../../../shared/types/user-account-groups.types';

interface IEditUserGroup {
  handleClose: () => void;
  loading: boolean;
  setLoading: (arg: boolean) => void;
  group: IAccountGroup;
  users: IWorkerSelectOption<IUserAccount>[];
}

interface IEditUserGroupForm {
  name: string;
  leaderId: number | null;
  leaderName: string | null;
  managerLogin: string;
  description?: string;
  workers: IWorkerSelectOption<IUserAccount>[];
  preliminaryWorkers: IWorkerSelectOption<IUserAccount>[];
  anotherGroup?: IGroupSelectOption | null;
  preliminaryAnotherGroup?: IGroupSelectOption | null;
}

const labelStyle = {
  color: '#00203399',
};

export const EditUserGroup: FC<IEditUserGroup> = ({handleClose, loading, setLoading, group, users}) => {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    getValues,
    trigger,
    formState: {isDirty},
    clearErrors,
    reset,
  } = useForm<IEditUserGroupForm>({
    mode: 'onChange',
    defaultValues: {
      name: group.name,
      description: group?.description,
      leaderName: group?.leader ? getFullName(group.leader) : null,
      leaderId: group?.leaderId || null,
      preliminaryWorkers: [],
      workers: users,
    },
  });
  // const {handleSearch, data, searchLoading} = useSearchStrategy<IWorkerSelectOption>(searchWorkersStrategy, true)
  const triggerGroupsQuery = useSetRecoilState(accountGroupsQueryTrigger);
  const [{usersToRegroup}, setUsersToRegroup] = useRecoilState(accountGroupUsersSelectionToRegroupState);
  const [editGroupMode, setEditGroupMode] = useRecoilState(editGroupModeState);
  const setCurrentGroup = useSetRecoilState(currentAccountGroupIdState);
  const setGroupControlMode = useSetRecoilState(accountGroupControlModeState);
  const ref = useRef(null);
  const [openDelete, setOpenDelete] = useState(false);
  const {bus} = useContext(EventBusContext);

  const usersToGroup = watch('workers');
  const preliminaryUsersToGroup = watch('preliminaryWorkers');
  const anotherGroup = watch('anotherGroup');
  const leaderId = watch('leaderId');

  const zeroUserToGroup = !usersToGroup.length;

  const toggleModalToDelete = () => {
    setOpenDelete((prev) => !prev);
  };

  const fromPreliminaryToFinal = () => {
    setValue('workers', [...usersToGroup, ...getValues('preliminaryWorkers')], {shouldDirty: true});
    setValue('preliminaryWorkers', []);
  };

  const removeUser = (id: number) => () => {
    setValue(
      'workers',
      getValues('workers').filter((i) => i.value.id !== id),
      {shouldDirty: true},
    );
    if (leaderId === id) {
      setValue('leaderName', '');
      setValue('leaderId', null, {shouldDirty: true});
    }
  };

  const assignLeader = (id: number, name: string) => () => {
    if (leaderId === id) {
      setValue('leaderId', null, {shouldDirty: true});
      setValue('leaderName', '', {shouldDirty: true});
    } else {
      setValue('leaderId', id, {shouldDirty: true});
      setValue('leaderName', name, {shouldDirty: true});
      trigger();
    }
  };

  const selectToRegroup = (row: IWorkerSelectOption<IUserAccount>, isSelected: boolean, isLeader: boolean) => () => {
    setUsersToRegroup((prev) =>
      isSelected
        ? {
            ...prev,
            usersToRegroup: prev.usersToRegroup.filter((i) => i !== row.value.id),
            isLeaderSelected: false,
          }
        : {
            ...prev,
            usersToRegroup: [...prev.usersToRegroup, row.value.id],
            isLeaderSelected: isLeader,
          },
    );
  };

  const handleDeleteGroup = async (setLoadingModal: (arg: boolean) => void) => {
    try {
      setLoadingModal(true);

      await AccountGroupsService.delete(group.id);

      triggerGroupsQuery((v) => v + 1);

      toast.success(`Группа "${group.name}" удалена`, {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });

      setLoadingModal(false);
      toggleModalToDelete();
      handleClose();
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoadingModal(false);
    }
  };

  const onSubmit: SubmitHandler<IEditUserGroupForm> = async (data, e) => {
    try {
      setLoading(true);

      const user =
        data.leaderId && group.leaderId !== data.leaderId
          ? await UserAccountService.getById(data.leaderId as any)
          : null; // TODO: доработать потом функционал групп (бригад)

      await AccountGroupsService.update({
        ...group,
        accounts: group.accounts.length && !data.workers.length ? [] : group.accounts,
        leader: user ? user.data : group.leader,
        leaderId: data.leaderId,
        name: data.name.trim(),
        description: data.description || null,
      } as IAccountGroup);

      data.workers.length &&
        (await AccountGroupsService.replaceGroupUsers(
          group.id,
          data.workers.map((i) => +i.value.id),
        ));

      toast.success(`Группа "${data.name}" изменена`, {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });

      setLoading(false);
      handleClose();
      triggerGroupsQuery((v) => v + 1);
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoading(false);
    }
  };

  const handleRegroup: SubmitHandler<IEditUserGroupForm> = async (data) => {
    if (!anotherGroup) return;

    try {
      setLoading(true);
      const usersToRegroupSet = new Set(usersToRegroup);

      usersToRegroupSet.size === group.accounts.length
        ? await AccountGroupsService.update({
            ...group,
            accounts: [],
          })
        : await AccountGroupsService.replaceGroupUsers(
            group.id,
            group.accounts.reduce<number[]>((prev, curr) => {
              return !usersToRegroupSet.has(curr.id) ? [...prev, curr.id] : prev;
            }, []),
          );
      await AccountGroupsService.addGroupUsers(anotherGroup.value.id, usersToRegroup);

      toast.success(`Группа "${data.name}" изменена`, {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });

      triggerGroupsQuery((v) => v + 1);
      handleClose();
    } catch (err) {
      bus?.emit('error', [err]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    zeroUserToGroup && clearErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zeroUserToGroup]);

  useEffect(() => {
    return () => {
      setUsersToRegroup({usersToRegroup: [], isLeaderSelected: false});
      setEditGroupMode('DEFAULT');
      setCurrentGroup(null);
      setGroupControlMode('ADD');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editGroupMode]);

  return (
    <>
      <form
        ref={ref}
        onSubmit={handleSubmit(editGroupMode === 'REGROUP' ? handleRegroup : onSubmit)}
        style={{height: '100%'}}
      >
        <Stack direction="column" spacing={1} sx={{justifyContent: 'space-between', height: '100%'}}>
          <Stack direction="column" spacing={2}>
            <Stack direction="row">
              <Typography variant="h5">Редактировать группу</Typography>
            </Stack>
            <Stack direction="column" spacing={0.25} sx={{mt: '32px !important'}}>
              <Typography sx={labelStyle} variant="subtitle2">
                Наименование группы
              </Typography>
              <ControlledInput
                control={control}
                rules={{
                  required: 'Обязательно для заполнения',
                }}
                name="name"
                disabled={group.isPredefined || loading || editGroupMode === 'REGROUP'}
                size="small"
              />
            </Stack>
            <Paper
              sx={{
                ...innerListStyle,
                overflowY: 'scroll',
                padding: '3px',
                height: 350,
              }}
            >
              {!!usersToGroup &&
                usersToGroup.map((i) => (
                  <UserRowOfGroup
                    key={i.value.id}
                    user={i}
                    selectToRegroup={selectToRegroup}
                    removeUser={removeUser(i.value.id)}
                    assignLeader={assignLeader(i.value.id, i.label)}
                    isLeader={leaderId === i.value.id}
                    isGroupPredefined={group.isPredefined}
                    loading={loading}
                  />
                ))}
            </Paper>
            <ButtonSwitcher
              group={group}
              loading={loading}
              fromPreliminaryToFinal={fromPreliminaryToFinal}
              control={control}
              usersToGroup={usersToGroup}
              defaultUsers={users}
              preliminaryUsersToGroup={preliminaryUsersToGroup}
              clearErrors={clearErrors}
              setValue={setValue}
              getValues={getValues}
              watch={watch}
            />
            {!group.isPredefined && (
              <Stack direction="column" spacing={0.25} sx={{mt: '20px !important'}}>
                <Typography sx={labelStyle} variant="subtitle2">
                  ФИО руководителя
                </Typography>
                <ControlledInput
                  control={control}
                  rules={{
                    required:
                      usersToGroup.length > 0 && editGroupMode !== 'REGROUP'
                        ? 'Назначьте руководителя из выбранных пользователей'
                        : false,
                  }}
                  conditionalSxError={{'& fieldset': {borderColor: '#d32f2f !important'}}}
                  name="leaderName"
                  disabled
                  size="small"
                />
              </Stack>
            )}
            <Stack direction="column" spacing={0.25}>
              <Typography sx={labelStyle} variant="subtitle2">
                Описание
              </Typography>
              <ControlledInput
                control={control}
                name="description"
                rules={{
                  maxLength: {
                    value: 255,
                    message: 'Максимум 255 символов',
                  },
                }}
                disabled={loading || editGroupMode === 'REGROUP'}
                size="small"
                rows={3}
                multiline
              />
            </Stack>
          </Stack>
          <Stack direction="row" sx={{justifyContent: 'space-between', mb: '32px !important'}}>
            <LoadingButton
              onClick={() => {
                (ref?.current as any).requestSubmit();
              }}
              variant="outlined"
              disabled={
                (preliminaryUsersToGroup.length > 0 && usersToGroup.length < 0) ||
                (editGroupMode === 'REGROUP' && !anotherGroup) ||
                !isDirty
              }
              color="success"
              loading={loading}
            >
              Сохранить изменения
            </LoadingButton>
            <LoadingButton
              loading={loading}
              onClick={toggleModalToDelete}
              variant="outlined"
              color="error"
              disabled={loading || editGroupMode === 'REGROUP'}
            >
              Удалить
            </LoadingButton>
          </Stack>
        </Stack>
      </form>
      {openDelete && (
        <ModalContainer
          sideEffect={handleDeleteGroup}
          renderModal={(sideEffect, loading) => (
            <ConfirmAction
              loading={loading}
              open={openDelete}
              handleClose={toggleModalToDelete}
              handleSideEffect={sideEffect}
              title="Подтвердите удаление"
              buttonTitle="Удалить"
            />
          )}
        />
      )}
    </>
  );
};
